/**
 * deviceQuery — filtros e ordenação da aba "Dispositivos" (NOC).
 *
 * DEVICES-FILTER-SORT-1. Módulo 100% PURO: sem JSX, sem DOM, sem fetch,
 * sem localStorage. É a ÚNICA fonte de verdade de "o que filtra" e "como
 * ordena" — a página só liga controles a este estado. Testável em node:
 *
 *   node src/lib/deviceQuery.test.js
 *
 * Por que cliente e não servidor: a frota real tem ~104 devices (93 ativos),
 * GET /api/admin/devices NÃO pagina e a lista inteira já chega a cada ciclo
 * de auto-refresh. Filtrar/ordenar aqui é instantâneo e NÃO encosta na query
 * que carrega os 3 gates do backend (archived_at IS NULL + authorization_status
 * + tenant scope) — o risco de vazamento cross-tenant fica estruturalmente
 * zero, em vez de "mitigado por revisão".
 *
 * REGRA DE OURO DA ORDENAÇÃO: valor ausente vai SEMPRE para o FIM, nas duas
 * direções, e todo comparador termina no desempate por device_id. Sem isso a
 * lista salta sozinha sob o cursor a cada heartbeat.
 */
import {
  deviceDisplayName,
  deviceStatus,
  heapAlertLevel,
  parseDate,
  pickPayload,
  rssiQuality,
} from "./format.js";
import { getProtectionState } from "./protection.js";
import { extractPowerMonitor } from "./powerMonitor.js";
import { CONN_STATE, connProbeView } from "./connProbe.js";

/** Valor "sem filtro" dos eixos de seleção. */
export const ALL = "all";

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

// ---------------------------------------------------------------------------
// Estado do painel (view). Tudo serializável — é o que vai para o localStorage.
// ---------------------------------------------------------------------------
export const DEVICE_QUERY_DEFAULTS = Object.freeze({
  // busca livre + status (faixa principal, já existiam na tela)
  q: "",
  status: ALL, // all | online | degraded | offline
  // instalação / ciclo de vida
  installedPeriod: ALL, // all | 7d | 30d | 90d | month | prevmonth | custom
  installedFrom: "", // yyyy-mm-dd (dia LOCAL, inclusivo)
  installedTo: "", // yyyy-mm-dd (dia LOCAL, inclusivo)
  installedConfirmed: false,
  deploymentType: ALL,
  ixc: ALL,
  noCustomer: false,
  // conectividade e sinal
  lastSeen: ALL,
  rssi: ALL,
  conn: ALL,
  relay: ALL,
  // firmware e segurança
  fwVersions: [],
  fwOutlier: false,
  fwProfile: ALL,
  securityFe: ALL,
  macAnchor: ALL,
  hmac: ALL,
  authStatus: ALL,
  // saúde
  heap: ALL,
  uptime: ALL,
  bootCountHigh: false,
  rebootReason: ALL,
  otaState: ALL,
  energyOnly: false,
  // proteção
  protectedOnly: false,
  protectionSource: ALL,
  // ordenação
  sortId: "name_asc",
});

// ---------------------------------------------------------------------------
// Data de instalação — DERIVADA (não existe coluna installed_at no legado).
//   installed_at (backend, vindo do device_binding mais ANTIGO)
//     └─ fallback: devices.created_at (NOT NULL, cobre 100% da frota)
// A origem é sempre exibida: data de cadastro é "estimada", nunca confirmada.
// ---------------------------------------------------------------------------
export const INSTALL_SOURCE = {
  BINDING: "binding",
  BINDING_CREATED: "binding_created",
  DEVICE_CREATED: "device_created",
};

export const INSTALL_SOURCE_LABEL = {
  [INSTALL_SOURCE.BINDING]: "confirmada pelo vínculo do cliente",
  [INSTALL_SOURCE.BINDING_CREATED]: "confirmada pelo vínculo (sem data de fechamento)",
  [INSTALL_SOURCE.DEVICE_CREATED]: "estimada pelo cadastro do dispositivo",
};

export function installDate(device) {
  const raw = device && device.installed_at;
  if (raw) {
    const d = parseDate(raw);
    if (d) {
      return {
        date: d,
        source: device.installed_at_source || INSTALL_SOURCE.BINDING,
      };
    }
  }
  const created = parseDate(device && device.created_at);
  return {
    date: created,
    source: created ? INSTALL_SOURCE.DEVICE_CREATED : null,
  };
}

export function isInstallDateConfirmed(device) {
  const s = installDate(device).source;
  return s === INSTALL_SOURCE.BINDING || s === INSTALL_SOURCE.BINDING_CREATED;
}

// ---------------------------------------------------------------------------
// Busca livre — o haystack precisa cobrir TUDO que a tela mostra. O operador
// digita o login PPPoE que está lendo na coluna Identificação; antes disto
// (haystack só com device_id/customer_code/fw/ip) ele não achava o device.
// ---------------------------------------------------------------------------
export function deviceHaystack(device) {
  if (!device) return "";
  return [
    device.device_id,
    device.pppoe_login,
    device.operational_identifier,
    device.customer_code,
    device.fw_version,
    device.ip_last,
    pickPayload(device, "ip"),
    pickPayload(device, "mac"),
    pickPayload(device, "qr_serial"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

// ---------------------------------------------------------------------------
// Classificadores (device → "balde"). Chave AUSENTE nunca cai num balde
// concreto: vira sempre "sem dado" — um bloco conn omitido pelo shed do
// firmware NÃO é falha de LAN.
// ---------------------------------------------------------------------------
function num(v) {
  const n = Number(v);
  return typeof v === "number" || (v !== null && v !== "" && !Number.isNaN(n))
    ? (Number.isFinite(n) ? n : null)
    : null;
}

export function lastSeenBucket(device, now = Date.now()) {
  const d = parseDate(device && device.last_seen);
  if (!d) return "never";
  const age = now - d.getTime();
  if (age <= HOUR_MS) return "le1h";
  if (age <= DAY_MS) return "le24h";
  if (age <= 7 * DAY_MS) return "le7d";
  if (age <= 30 * DAY_MS) return "le30d";
  return "gt30d";
}

const LAST_SEEN_ORDER = ["le1h", "le24h", "le7d", "le30d", "gt30d", "never"];

/** Seleção CUMULATIVA: "sem batimento há mais de 24 h" inclui 7 d e 30 d. */
function lastSeenMatches(bucket, value) {
  if (value === "le1h") return bucket === "le1h";
  if (value === "never") return bucket === "never";
  const from = { gt1h: 1, gt24h: 2, gt7d: 3, gt30d: 4 }[value];
  if (from == null) return true;
  const idx = LAST_SEEN_ORDER.indexOf(bucket);
  return bucket === "never" || (idx >= from && idx < LAST_SEEN_ORDER.length - 1);
}

export function uptimeBucket(device) {
  const s = num(pickPayload(device, "uptime_s"));
  if (s == null) return "unknown";
  if (s < 3600) return "lt1h";
  if (s < 86400) return "lt24h";
  if (s < 7 * 86400) return "lt7d";
  return "gt7d";
}

export function connBucket(device) {
  const view = connProbeView(pickPayload(device, "conn"));
  if (!view.supported) return "nodata";
  if (view.overall === CONN_STATE.FAIL) {
    if (view.lan.state === CONN_STATE.FAIL) return "fail_lan";
    if (view.inet.state === CONN_STATE.FAIL) return "fail_inet";
    return "fail_dns";
  }
  if (view.overall === CONN_STATE.STALE) return "stale";
  if (view.overall === CONN_STATE.OK) return "ok";
  return "nodata";
}

export function securityFeBucket(device) {
  const sec = pickPayload(device, "security");
  if (!sec || typeof sec !== "object" || sec.fe == null) return "nosignal";
  return sec.fe ? "fe" : "plain";
}

export function fwProfileBucket(device) {
  const sec = pickPayload(device, "security");
  const p = sec && typeof sec === "object" ? sec.profile : null;
  return p || "unknown";
}

export function macAnchorBucket(device) {
  if (pickPayload(device, "clone_suspected") === true) return "suspect";
  const st = pickPayload(device, "mac_anchor_status");
  return st || "unknown";
}

export function hmacBucket(device) {
  if (device && device.hmac_secret_pending_set) return "pending";
  if (device && device.hmac_secret_set) return "set";
  return "none";
}

export function otaStateBucket(device) {
  const diag = pickPayload(device, "cmd_diag");
  const st = diag && typeof diag === "object" ? diag.ota_state : null;
  const err = diag && typeof diag === "object" ? diag.ota_last_error : null;
  if (err) return "error";
  return st || "unknown";
}

export function relayBucket(device) {
  const r = pickPayload(device, "relay");
  if (r === "on" || r === true) return "on";
  if (r === "off" || r === false) return "off";
  return "unknown";
}

export function ixcBucket(device, ixcBoundIds) {
  if (!ixcBoundIds || typeof ixcBoundIds.has !== "function") return "unknown";
  return ixcBoundIds.has(device && device.device_id) ? "bound" : "unbound";
}

export function hasCustomerLink(device) {
  if (!device) return false;
  return Boolean(
    (device.pppoe_login || "").trim()
      || (device.customer_code || "").trim()
      || device.customer_id
      || (device.operational_identifier || "").trim(),
  );
}

export function deploymentBucket(device) {
  const v = device && device.deployment_type;
  return v || "unset";
}

// ---------------------------------------------------------------------------
// Contexto derivado da PRÓPRIA lista (nunca hardcoded): versão de firmware
// majoritária e limiar dinâmico de reinícios. Recalculado quando a fonte muda.
// ---------------------------------------------------------------------------
export function buildFilterContext(source = []) {
  const list = Array.isArray(source) ? source : [];
  const fwCount = new Map();
  const boots = [];
  for (const d of list) {
    const fw = d && d.fw_version;
    if (fw) fwCount.set(fw, (fwCount.get(fw) || 0) + 1);
    const b = num(pickPayload(d, "boot_count"));
    if (b != null) boots.push(b);
  }
  let fwMode = null;
  let best = 0;
  for (const [fw, n] of fwCount) {
    if (n > best) {
      best = n;
      fwMode = fw;
    }
  }
  boots.sort((a, b) => a - b);
  // nearest-rank. Com floor(n*0.9) o índice vira n-1 para qualquer n ≤ 10 —
  // o "p90" seria o MÁXIMO da amostra e `boot_count > limite` não casaria com
  // ninguém (o eixo sumia em visões pequenas, como um tenant com 8 devices).
  let p90 = null;
  if (boots.length) {
    const idx = Math.max(0, Math.ceil(boots.length * 0.9) - 1);
    p90 = boots[idx];
    // amostra sem dispersão (todo mundo igual) não tem outlier a marcar
    if (p90 <= boots[0]) p90 = null;
  }
  return { fwMode, fwModeCount: best, bootCountP90: p90, total: list.length };
}

export function isFwOutlier(device, ctx) {
  if (!ctx || !ctx.fwMode) return false;
  const fw = device && device.fw_version;
  return Boolean(fw) && fw !== ctx.fwMode;
}

export function isBootCountHigh(device, ctx) {
  const limit = ctx && ctx.bootCountP90;
  if (limit == null) return false;
  const b = num(pickPayload(device, "boot_count"));
  // >= e não >: em amostra pequena o p90 É o próprio topo (8 devices, um com
  // 47 reinícios → p90 = 47), e com > ninguém casaria — o eixo sumiria justo
  // na visão onde o instável é mais visível.
  return b != null && b >= limit;
}

// ---------------------------------------------------------------------------
// Período de instalação. Comparação por DIA LOCAL (pt-BR) e limites
// INCLUSIVOS nas duas pontas — quem escolhe 03/09 precisa ver o device
// instalado às 14h40 de 03/09.
// ---------------------------------------------------------------------------
export function localDayStart(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0).getTime();
}

export function localDayEnd(value) {
  const start = localDayStart(value);
  if (start == null) return null;
  const d = new Date(start);
  d.setDate(d.getDate() + 1);
  return d.getTime() - 1;
}

export const INSTALLED_PERIODS = [
  { value: ALL, label: "Qualquer data" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "90d", label: "Últimos 90 dias" },
  { value: "month", label: "Este mês" },
  { value: "prevmonth", label: "Mês passado" },
  { value: "custom", label: "Período personalizado" },
];

/** @returns {{from:number|null, to:number|null}} janela em ms (inclusiva). */
export function installedRange(view, now = Date.now()) {
  const period = (view && view.installedPeriod) || ALL;
  if (period === "custom") {
    return {
      from: localDayStart(view && view.installedFrom),
      to: localDayEnd(view && view.installedTo),
    };
  }
  if (period === "7d") return { from: now - 7 * DAY_MS, to: null };
  if (period === "30d") return { from: now - 30 * DAY_MS, to: null };
  if (period === "90d") return { from: now - 90 * DAY_MS, to: null };
  if (period === "month") {
    const d = new Date(now);
    return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), to: null };
  }
  if (period === "prevmonth") {
    const d = new Date(now);
    return {
      from: new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(),
      to: new Date(d.getFullYear(), d.getMonth(), 1).getTime() - 1,
    };
  }
  return { from: null, to: null };
}

function installedMatches(device, view, now) {
  const { from, to } = installedRange(view, now);
  if (from == null && to == null) return true;
  const { date } = installDate(device);
  if (!date) return false;
  const t = date.getTime();
  if (from != null && t < from) return false;
  if (to != null && t > to) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Registro declarativo dos eixos. Um único lugar define: rótulo, controle,
// opções, como casa e como aparece no chip de filtro ativo. Assim nenhum eixo
// futuro fica órfão do contador nem do "Limpar filtros".
// ---------------------------------------------------------------------------
const OPT = (value, label) => ({ value, label });

/** Rótulos PT-BR dos perfis de firmware conhecidos; o resto sai como veio. */
export const FW_PROFILE_LABEL = {
  client: "Cliente",
  "provider-client": "Cliente (provedor)",
  "tower-core": "Torre (core)",
  "tower-energy": "Torre (energia)",
  "commercial-normal": "Comercial",
  "commercial-energy": "Comercial (energia)",
  unknown: "Sem sinal de perfil",
};

export const FILTER_GROUPS = [
  { id: "install", label: "Instalação e ciclo de vida" },
  { id: "conn", label: "Conectividade e sinal" },
  { id: "fw", label: "Firmware e segurança" },
  { id: "health", label: "Saúde do dispositivo" },
];

export const FILTER_AXES = [
  // ---- instalação e ciclo de vida ----
  {
    key: "installedConfirmed",
    group: "install",
    label: "Só data de instalação confirmada",
    control: "toggle",
    bucket: (d) => isInstallDateConfirmed(d),
    chip: () => "Data de instalação confirmada",
    // sem sinal do backend a frota inteira é "estimada" — o controle some
    // em vez de zerar a lista.
    available: (source) => source.some((d) => d && d.installed_at),
  },
  {
    key: "deploymentType",
    group: "install",
    label: "Tipo de ponto",
    control: "select",
    bucket: deploymentBucket,
    options: [
      OPT("customer", "Cliente"),
      OPT("infrastructure", "Infraestrutura (torre/POP)"),
      OPT("unset", "Sem classificação"),
    ],
    chip: (v, opt) => `Tipo de ponto: ${opt}`,
    available: (source) => source.some((d) => d && d.deployment_type),
  },
  {
    key: "ixc",
    group: "install",
    label: "Vínculo IXC",
    control: "select",
    bucket: (d, ctx, extras) => ixcBucket(d, extras && extras.ixcBoundIds),
    // sem o Set de bindings (o fetch falha em silêncio) o balde é "unknown" e
    // o eixo NÃO esconde ninguém — não saber não pode virar "sem vínculo".
    match: (bucket, value) => bucket === "unknown" || bucket === value,
    options: [OPT("bound", "Com vínculo"), OPT("unbound", "Sem vínculo")],
    chip: (v, opt) => `Vínculo IXC: ${opt}`,
    // o fetch de bindings falha em silêncio; com o Set vazio "sem vínculo"
    // contaria a frota inteira e mentiria.
    available: (source, ctx, extras) =>
      Boolean(extras && extras.ixcBoundIds && extras.ixcBoundIds.size > 0),
  },
  {
    key: "noCustomer",
    group: "install",
    label: "Sem cliente vinculado",
    control: "toggle",
    bucket: (d) => !hasCustomerLink(d),
    chip: () => "Sem cliente vinculado",
  },
  // ---- conectividade e sinal ----
  {
    key: "lastSeen",
    group: "conn",
    label: "Último batimento",
    control: "select",
    bucket: (d, ctx, extras) => lastSeenBucket(d, extras && extras.now),
    match: (bucket, value) => lastSeenMatches(bucket, value),
    options: [
      OPT("le1h", "Visto na última hora"),
      OPT("gt1h", "Sem batimento há mais de 1 h"),
      OPT("gt24h", "Sem batimento há mais de 24 h"),
      OPT("gt7d", "Sem batimento há mais de 7 dias"),
      OPT("gt30d", "Sem batimento há mais de 30 dias"),
      OPT("never", "Nunca comunicou"),
    ],
    chip: (v, opt) => opt,
  },
  {
    key: "rssi",
    group: "conn",
    label: "Qualidade de sinal (RSSI)",
    control: "select",
    bucket: (d) => rssiQuality(pickPayload(d, "rssi")),
    match: (bucket, value) =>
      value === "bad" ? bucket === "fair" || bucket === "poor" : bucket === value,
    options: [
      OPT("bad", "Ruim ou regular"),
      OPT("poor", "Ruim (< -75 dBm)"),
      OPT("fair", "Regular (-75 a -65 dBm)"),
      OPT("good", "Bom (-65 a -55 dBm)"),
      OPT("excellent", "Ótimo (≥ -55 dBm)"),
      OPT("unknown", "Sem dado de RSSI"),
    ],
    chip: (v, opt) => `RSSI: ${opt}`,
  },
  {
    key: "conn",
    group: "conn",
    label: "Sondagem de conectividade",
    control: "select",
    bucket: connBucket,
    match: (bucket, value) =>
      value === "fail" ? String(bucket).startsWith("fail_") : bucket === value,
    options: [
      OPT("fail", "Qualquer falha"),
      OPT("fail_lan", "Falha de LAN"),
      OPT("fail_inet", "Falha de Internet"),
      OPT("fail_dns", "Falha de DNS"),
      OPT("stale", "Dados antigos"),
      OPT("ok", "Sondagem OK"),
      OPT("nodata", "Sem dados no último batimento"),
    ],
    chip: (v, opt) => `Conectividade: ${opt}`,
  },
  {
    key: "relay",
    group: "conn",
    label: "Relé",
    control: "select",
    bucket: relayBucket,
    options: [OPT("off", "Desligado"), OPT("on", "Ligado"), OPT("unknown", "Sem dado")],
    chip: (v, opt) => `Relé: ${opt}`,
  },
  // ---- firmware e segurança ----
  {
    key: "fwVersions",
    group: "fw",
    label: "Versão de firmware",
    control: "multi",
    bucket: (d) => (d && d.fw_version) || null,
    derived: true,
    chip: (v) => `Firmware: ${v.join(", ")}`,
  },
  {
    key: "fwOutlier",
    group: "fw",
    label: "Firmware fora do padrão da frota",
    control: "toggle",
    bucket: (d, ctx) => isFwOutlier(d, ctx),
    chip: (v, opt, ctx) =>
      `Firmware ≠ ${(ctx && ctx.fwMode) || "padrão"}`,
    available: (source, ctx) => Boolean(ctx && ctx.fwMode),
  },
  {
    key: "fwProfile",
    group: "fw",
    label: "Perfil do firmware",
    control: "select",
    bucket: fwProfileBucket,
    // DERIVADO dos valores que o firmware realmente reporta. Lista fixa não
    // serve: a frota tem 'commercial-normal' (que não estava na lista) e cada
    // perfil novo do firmware entraria mudo, sem opção nenhuma.
    derived: true,
    optionLabel: (v) => FW_PROFILE_LABEL[v] || v,
    chip: (v, opt) => `Perfil: ${opt}`,
  },
  {
    key: "securityFe",
    group: "fw",
    label: "Flash Encryption",
    control: "select",
    bucket: securityFeBucket,
    options: [
      OPT("fe", "Flash Encryption ativa"),
      OPT("plain", "Texto plano"),
      OPT("nosignal", "Sem sinal de segurança"),
    ],
    chip: (v, opt) => `Segurança: ${opt}`,
  },
  {
    key: "macAnchor",
    group: "fw",
    label: "Anti-clonagem (MAC anchor)",
    control: "select",
    bucket: macAnchorBucket,
    options: [
      OPT("suspect", "Clone suspeito"),
      OPT("mismatch", "MAC divergente"),
      OPT("ok", "Ancorado e conferindo"),
      OPT("unanchored", "Sem âncora"),
      OPT("unknown", "Sem dado"),
    ],
    chip: (v, opt) => `Anti-clonagem: ${opt}`,
  },
  {
    key: "hmac",
    group: "fw",
    label: "Canal seguro (HMAC)",
    control: "select",
    bucket: hmacBucket,
    options: [
      OPT("set", "Com secret"),
      OPT("pending", "Rotação pendente"),
      OPT("none", "Sem secret"),
    ],
    chip: (v, opt) => `HMAC: ${opt}`,
  },
  {
    key: "authStatus",
    group: "fw",
    label: "Autorização",
    control: "select",
    bucket: (d) => (d && d.authorization_status) || "authorized",
    options: [
      OPT("authorized", "Autorizado"),
      OPT("pending", "Pendente"),
      OPT("blocked", "Bloqueado"),
    ],
    chip: (v, opt) => `Autorização: ${opt}`,
    // só faz sentido na visão que revela pendentes/bloqueados
    available: (source) =>
      source.some((d) => d && d.authorization_status && d.authorization_status !== "authorized"),
  },
  // ---- saúde ----
  {
    key: "heap",
    group: "health",
    label: "Heap livre",
    control: "select",
    bucket: (d) => heapAlertLevel(num(pickPayload(d, "heap_free"))),
    options: [
      OPT("crit", "Crítico (< 50 KB)"),
      OPT("warn", "Atenção (< 80 KB)"),
      OPT("ok", "Saudável (≥ 80 KB)"),
      OPT("unknown", "Sem dado"),
    ],
    chip: (v, opt) => `Heap: ${opt}`,
  },
  {
    key: "uptime",
    group: "health",
    label: "Tempo ativo",
    control: "select",
    bucket: uptimeBucket,
    options: [
      OPT("lt1h", "Reiniciou há menos de 1 h"),
      OPT("lt24h", "Menos de 24 h ativo"),
      OPT("lt7d", "Menos de 7 dias ativo"),
      OPT("gt7d", "Mais de 7 dias ativo"),
      OPT("unknown", "Sem dado"),
    ],
    chip: (v, opt) => `Tempo ativo: ${opt}`,
  },
  {
    key: "bootCountHigh",
    group: "health",
    label: "Muitos reinícios",
    control: "toggle",
    bucket: (d, ctx) => isBootCountHigh(d, ctx),
    chip: (v, opt, ctx) =>
      `Reinícios ≥ ${(ctx && ctx.bootCountP90) != null ? ctx.bootCountP90 : "—"}`,
    available: (source, ctx) => (ctx && ctx.bootCountP90) != null,
  },
  {
    key: "rebootReason",
    group: "health",
    label: "Motivo do último reinício",
    control: "select",
    bucket: (d) =>
      (d && d.last_boot_reason) || pickPayload(d, "reboot_reason") || null,
    derived: true,
    chip: (v, opt) => `Reinício: ${opt || v}`,
  },
  {
    key: "otaState",
    group: "health",
    label: "Estado de OTA",
    control: "select",
    bucket: otaStateBucket,
    options: [
      OPT("error", "Com erro de OTA"),
      OPT("idle", "Ocioso"),
      OPT("downloading", "Baixando"),
      OPT("applying", "Aplicando"),
      OPT("unknown", "Sem dado"),
    ],
    chip: (v, opt) => `OTA: ${opt}`,
  },
  {
    key: "energyOnly",
    group: "health",
    label: "Com medição de energia (INA226)",
    control: "toggle",
    bucket: (d) => extractPowerMonitor(d && d.last_hb_payload) != null,
    chip: () => "Com medição de energia",
  },
  {
    key: "protectionSource",
    group: "health",
    label: "Origem da proteção",
    control: "select",
    bucket: (d) => getProtectionState(d).source || "none",
    options: [
      OPT("production", "Produção"),
      OPT("reference", "Referência"),
      OPT("manual", "Manual"),
      OPT("env", "Denylist do ambiente"),
    ],
    chip: (v, opt) => `Proteção: ${opt}`,
    available: (source) => source.some((d) => getProtectionState(d).protected),
  },
];

const AXIS_BY_KEY = new Map(FILTER_AXES.map((a) => [a.key, a]));

/** Eixos visíveis para a fonte atual (esconde o que não tem dado real). */
export function visibleAxes(source = [], ctx = null, extras = {}) {
  return FILTER_AXES.filter(
    (a) => typeof a.available !== "function" || a.available(source, ctx, extras),
  );
}

function axisMatches(axis, device, value, ctx, extras) {
  // Curto-circuito ANTES de classificar: eixo no default não filtra, e
  // calcular o balde à toa custaria ~25 classificações por device a cada
  // tecla digitada na busca.
  if (axis.control === "toggle") {
    if (value !== true) return true;
    return axis.bucket(device, ctx, extras) === true;
  }
  if (axis.control === "multi") {
    if (!Array.isArray(value) || value.length === 0) return true;
    return value.includes(axis.bucket(device, ctx, extras));
  }
  if (value === ALL || value == null) return true;
  const bucket = axis.bucket(device, ctx, extras);
  return axis.match ? axis.match(bucket, value) : bucket === value;
}

/** Combinação AND de todos os eixos ativos. Eixo no default nunca filtra. */
export function matchesDeviceQuery(device, view, ctx = null, extras = {}) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  const now = extras.now || Date.now();

  const q = String(v.q || "").trim().toLowerCase();
  if (q && !deviceHaystack(device).includes(q)) return false;

  if (v.status !== ALL && deviceStatus(device, now) !== v.status) return false;

  if (v.protectedOnly && !getProtectionState(device).protected) return false;

  if (!installedMatches(device, v, now)) return false;

  for (const axis of FILTER_AXES) {
    if (!axisMatches(axis, device, v[axis.key], ctx, extras)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Opções DERIVADAS da própria lista, com contagem. Nada hardcoded: versão de
// firmware e motivo de reboot novos aparecem sozinhos; opção com contagem 0
// some (senão parece filtro quebrado).
// ---------------------------------------------------------------------------
export function buildFilterOptions(source = [], ctx = null, extras = {}) {
  const list = Array.isArray(source) ? source : [];
  const out = {};
  for (const axis of FILTER_AXES) {
    const counts = new Map();
    for (const d of list) {
      const bucket = axis.bucket(d, ctx, extras);
      if (axis.control === "toggle") {
        if (bucket === true) counts.set(true, (counts.get(true) || 0) + 1);
        continue;
      }
      if (bucket == null || bucket === "") continue;
      counts.set(bucket, (counts.get(bucket) || 0) + 1);
    }
    if (axis.control === "toggle") {
      out[axis.key] = { count: counts.get(true) || 0 };
      continue;
    }
    if (axis.derived) {
      const values = [...counts.entries()].map(([value, count]) => ({
        value,
        label: axis.optionLabel ? axis.optionLabel(value) : value,
        count,
      }));
      values.sort(
        axis.key === "fwVersions"
          ? (a, b) => compareFwVersion(b.value, a.value)
          : (a, b) => b.count - a.count,
      );
      out[axis.key] = { options: values };
      continue;
    }
    // CONTAR PRIMEIRO, PODAR DEPOIS. Opções AGREGADORAS ("sem batimento há
    // mais de 24 h", "qualquer falha", "sinal ruim") não são nomes de balde:
    // seu valor nunca aparece no mapa `counts`. Podar por counts.get() antes
    // do recount apagava justamente os cortes de plantão mais úteis — e o
    // eixo inteiro sumia do painel por ter menos de 2 opções.
    const opts = (axis.options || [])
      .map((o) => ({
        ...o,
        count: axis.match
          ? list.reduce(
            (n, d) => n + (axis.match(axis.bucket(d, ctx, extras), o.value) ? 1 : 0),
            0,
          )
          : counts.get(o.value) || 0,
      }))
      .filter((o) => o.count > 0);
    out[axis.key] = { options: opts };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Comparadores. Ausente SEMPRE no fim (nas duas direções) e desempate final
// por device_id — é isso que impede a lista de saltar a cada auto-refresh.
// ---------------------------------------------------------------------------
function tie(a, b) {
  return String((a && a.device_id) || "").localeCompare(
    String((b && b.device_id) || ""),
    "pt-BR",
    { numeric: true, sensitivity: "base" },
  );
}

function isMissing(v) {
  return (
    v == null
    || v === ""
    || (typeof v === "number" && !Number.isFinite(v))
  );
}

function makeCompare(get, { dir = "asc", kind = "number", then = null } = {}) {
  return (a, b) => {
    const va = get(a);
    const vb = get(b);
    const ma = isMissing(va);
    const mb = isMissing(vb);
    if (ma && mb) return then ? then(a, b) || tie(a, b) : tie(a, b);
    if (ma) return 1;
    if (mb) return -1;
    let c;
    if (kind === "text") {
      c = String(va).localeCompare(String(vb), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      });
    } else {
      c = va < vb ? -1 : va > vb ? 1 : 0;
    }
    if (dir === "desc") c = -c;
    return c || (then ? then(a, b) || tie(a, b) : tie(a, b));
  };
}

function parseFw(value) {
  const m = /^v?(\d+)\.(\d+)\.(\d+)(.*)$/.exec(String(value || "").trim());
  if (!m) return null;
  return { n: [+m[1], +m[2], +m[3]], suffix: m[4] || "" };
}

/**
 * Ordem crescente de firmware (mais antigo primeiro). localeCompare puro erra
 * feio aqui: colocaria 2.8.9 acima de 2.8.11 e 2.10 abaixo de 2.9.
 * Versão não parseável fica DEPOIS de toda versão parseável.
 */
export function compareFwVersion(a, b) {
  const pa = parseFw(a);
  const pb = parseFw(b);
  if (pa && pb) {
    for (let i = 0; i < 3; i += 1) {
      if (pa.n[i] !== pb.n[i]) return pa.n[i] - pb.n[i];
    }
    return pa.suffix.localeCompare(pb.suffix, "pt-BR", { sensitivity: "base" });
  }
  if (pa) return -1;
  if (pb) return 1;
  return String(a || "").localeCompare(String(b || ""), "pt-BR", {
    sensitivity: "base",
  });
}

function fwCompare(dir) {
  return (a, b) => {
    const va = a && a.fw_version;
    const vb = b && b.fw_version;
    const ma = isMissing(va);
    const mb = isMissing(vb);
    if (ma && mb) return tie(a, b);
    if (ma) return 1;
    if (mb) return -1;
    const pa = parseFw(va);
    const pb = parseFw(vb);
    // não parseável fica no fim em QUALQUER direção (não é "versão zero")
    if (!pa && pb) return 1;
    if (pa && !pb) return -1;
    let c = compareFwVersion(va, vb);
    if (dir === "desc") c = -c;
    return c || tie(a, b);
  };
}

const STATUS_RANK = { offline: 0, degraded: 1, online: 2 };
const PROTECTION_RANK = { production: 0, reference: 1, manual: 2, env: 3 };
const RELAY_RANK = { off: 0, on: 1, unknown: 2 };

const timeOf = (value) => {
  const d = parseDate(value);
  return d ? d.getTime() : null;
};

export const SORT_GROUPS = [
  { id: "identity", label: "Identificação" },
  { id: "time", label: "Tempo" },
  { id: "firmware", label: "Firmware" },
  { id: "health", label: "Saúde" },
  { id: "ops", label: "Operação" },
];

export const SORT_OPTIONS = [
  {
    id: "name_asc",
    group: "identity",
    label: "Identificação (A → Z)",
    compare: makeCompare((d) => deviceDisplayName(d).label, { kind: "text" }),
  },
  {
    id: "name_desc",
    group: "identity",
    label: "Identificação (Z → A)",
    compare: makeCompare((d) => deviceDisplayName(d).label, {
      kind: "text",
      dir: "desc",
    }),
  },
  {
    id: "device_id_asc",
    group: "identity",
    label: "device_id (A → Z)",
    compare: makeCompare((d) => d && d.device_id, { kind: "text" }),
  },
  {
    id: "customer_asc",
    group: "identity",
    label: "Cliente (A → Z)",
    compare: makeCompare((d) => d && d.customer_code, { kind: "text" }),
  },
  {
    id: "last_seen_desc",
    group: "time",
    label: "Último batimento (mais recente)",
    compare: makeCompare((d) => timeOf(d && d.last_seen), { dir: "desc" }),
  },
  {
    id: "last_seen_asc",
    group: "time",
    label: "Último batimento (mais antigo)",
    compare: makeCompare((d) => timeOf(d && d.last_seen)),
  },
  {
    id: "installed_desc",
    group: "time",
    label: "Instalado em (mais recente)",
    compare: makeCompare((d) => {
      const { date } = installDate(d);
      return date ? date.getTime() : null;
    }, { dir: "desc" }),
  },
  {
    id: "installed_asc",
    group: "time",
    label: "Instalado em (mais antigo)",
    compare: makeCompare((d) => {
      const { date } = installDate(d);
      return date ? date.getTime() : null;
    }),
  },
  {
    id: "created_desc",
    group: "time",
    label: "Cadastrado no sistema (mais recente)",
    compare: makeCompare((d) => timeOf(d && d.created_at), { dir: "desc" }),
  },
  {
    id: "uptime_asc",
    group: "time",
    label: "Tempo ativo (menor primeiro)",
    compare: makeCompare((d) => num(pickPayload(d, "uptime_s"))),
  },
  {
    id: "fw_asc",
    group: "firmware",
    label: "Firmware (mais antigo primeiro)",
    compare: fwCompare("asc"),
  },
  {
    id: "fw_desc",
    group: "firmware",
    label: "Firmware (mais novo primeiro)",
    compare: fwCompare("desc"),
  },
  {
    id: "rssi_asc",
    group: "health",
    label: "Sinal RSSI (pior primeiro)",
    compare: makeCompare((d) => num(pickPayload(d, "rssi"))),
  },
  {
    id: "heap_asc",
    group: "health",
    label: "Heap livre (menor primeiro)",
    compare: makeCompare((d) => num(pickPayload(d, "heap_free"))),
  },
  {
    id: "boot_count_desc",
    group: "health",
    label: "Reinícios (mais instável primeiro)",
    compare: makeCompare((d) => num(pickPayload(d, "boot_count")), {
      dir: "desc",
    }),
  },
  {
    id: "wifi_drops_desc",
    group: "health",
    label: "Quedas de Wi-Fi (mais instável primeiro)",
    compare: makeCompare((d) => num(pickPayload(d, "wifi_disconnects")), {
      dir: "desc",
    }),
  },
  {
    id: "status_rank",
    group: "ops",
    label: "Criticidade (offline primeiro)",
    compare: (a, b) => {
      const ra = STATUS_RANK[deviceStatus(a)] ?? 3;
      const rb = STATUS_RANK[deviceStatus(b)] ?? 3;
      if (ra !== rb) return ra - rb;
      // dentro do grupo, o mais mudo aparece primeiro
      return makeCompare((d) => timeOf(d && d.last_seen))(a, b);
    },
  },
  {
    id: "protected_first",
    group: "ops",
    label: "Protegidos primeiro",
    compare: (a, b) => {
      const pa = getProtectionState(a);
      const pb = getProtectionState(b);
      if (pa.protected !== pb.protected) return pa.protected ? -1 : 1;
      const ra = PROTECTION_RANK[pa.source] ?? 9;
      const rb = PROTECTION_RANK[pb.source] ?? 9;
      return ra - rb || tie(a, b);
    },
  },
  {
    id: "relay_off_first",
    group: "ops",
    label: "Relé desligado primeiro",
    compare: (a, b) => {
      const ra = RELAY_RANK[relayBucket(a)] ?? 3;
      const rb = RELAY_RANK[relayBucket(b)] ?? 3;
      return ra - rb || tie(a, b);
    },
  },
];

const SORT_BY_ID = new Map(SORT_OPTIONS.map((s) => [s.id, s]));

export const DEFAULT_SORT_ID = DEVICE_QUERY_DEFAULTS.sortId;
/** Ordem histórica do backend (last_seen DESC NULLS LAST), a 1 clique. */
export const TRIAGE_SORT_ID = "last_seen_desc";

export function getSortOption(sortId) {
  return SORT_BY_ID.get(sortId) || SORT_BY_ID.get(DEFAULT_SORT_ID);
}

export function sortDevices(source = [], sortId = DEFAULT_SORT_ID) {
  const opt = getSortOption(sortId);
  // cópia: a prop `devices` do App é reusada entre renders, nunca mutar
  return [...(Array.isArray(source) ? source : [])].sort(opt.compare);
}

// ---------------------------------------------------------------------------
// Atalhos de plantão — combinações prontas dos eixos acima. Aplicar um atalho
// SUBSTITUI os filtros (a ordenação escolhida é preservada), para o operador
// nunca herdar um filtro invisível de outro atalho.
// ---------------------------------------------------------------------------
export const DEVICE_PRESETS = [
  {
    id: "offline7d",
    label: "Offline há mais de 7 dias",
    always: true,
    patch: { lastSeen: "gt7d" },
  },
  {
    id: "silent30d",
    label: "Sumidos há mais de 30 dias",
    patch: { lastSeen: "gt30d" },
  },
  {
    id: "newSilent",
    label: "Instalados nos últimos 30 dias e mudos",
    always: true,
    patch: { installedPeriod: "30d", lastSeen: "gt24h" },
  },
  {
    id: "fwOutlier",
    label: "Firmware fora do padrão",
    patch: { fwOutlier: true },
  },
  {
    id: "noCustomer",
    label: "Sem cliente vinculado",
    always: true,
    patch: { noCustomer: true },
  },
  {
    id: "badSignal",
    label: "Sinal fraco",
    patch: { rssi: "bad" },
  },
  {
    id: "unstable",
    label: "Muitos reinícios",
    patch: { bootCountHigh: true },
  },
  {
    id: "protected",
    label: "Protegidos",
    always: true,
    patch: { protectedOnly: true },
  },
];

const FILTER_KEYS = Object.keys(DEVICE_QUERY_DEFAULTS).filter(
  (k) => k !== "sortId",
);

function sameValue(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

/** Estado com TODOS os filtros no default; a ordenação escolhida sobrevive. */
export function clearFilters(view) {
  return { ...DEVICE_QUERY_DEFAULTS, sortId: (view && view.sortId) || DEFAULT_SORT_ID };
}

export function applyPreset(view, presetId) {
  const preset = DEVICE_PRESETS.find((p) => p.id === presetId);
  if (!preset) return clearFilters(view);
  // a BUSCA sobrevive ao atalho: ela é do operador, não do recorte. Sem isto,
  // digitar no campo desmarcava o chip e reclicá-lo apagava o que foi digitado.
  return { ...clearFilters(view), q: (view && view.q) || "", ...preset.patch };
}

/** Atalho ativo = estado idêntico ao que ele aplicaria (a busca não conta). */
export function activePresetId(view) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  for (const preset of DEVICE_PRESETS) {
    const target = { ...DEVICE_QUERY_DEFAULTS, ...preset.patch };
    const keys = FILTER_KEYS.filter((k) => k !== "q");
    if (keys.every((k) => sameValue(v[k], target[k]))) return preset.id;
  }
  return null;
}

export function countPreset(source, presetId, ctx, extras) {
  const view = applyPreset(DEVICE_QUERY_DEFAULTS, presetId);
  return (Array.isArray(source) ? source : []).reduce(
    (n, d) => n + (matchesDeviceQuery(d, view, ctx, extras) ? 1 : 0),
    0,
  );
}

/** Atalhos que valem a pena mostrar: contagem > 0, ou marcados como `always`. */
export function visiblePresets(source, ctx, extras) {
  return DEVICE_PRESETS.map((p) => ({
    ...p,
    count: countPreset(source, p.id, ctx, extras),
  })).filter((p) => p.always || p.count > 0);
}

export function isAxisActive(view, key) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  return !sameValue(v[key], DEVICE_QUERY_DEFAULTS[key]);
}

/** "Personalizado" sem nenhuma das duas datas não recorta nada — não conta. */
function installedActive(v) {
  if (v.installedPeriod === "custom") {
    return Boolean(v.installedFrom || v.installedTo);
  }
  return v.installedPeriod !== ALL || Boolean(v.installedFrom || v.installedTo);
}

export function activeFilterCount(view) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  let n = 0;
  for (const key of FILTER_KEYS) {
    if (key === "installedFrom" || key === "installedTo" || key === "installedPeriod") {
      continue;
    }
    if (!sameValue(v[key], DEVICE_QUERY_DEFAULTS[key])) n += 1;
  }
  if (installedActive(v)) n += 1;
  return n;
}

function periodLabel(view) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  if (v.installedPeriod === "custom" || (v.installedFrom || v.installedTo)) {
    const from = v.installedFrom ? v.installedFrom.split("-").reverse().join("/") : null;
    const to = v.installedTo ? v.installedTo.split("-").reverse().join("/") : null;
    if (from && to) return `Instalado de ${from} a ${to}`;
    if (from) return `Instalado a partir de ${from}`;
    if (to) return `Instalado até ${to}`;
    return "Instalado: período personalizado";
  }
  const opt = INSTALLED_PERIODS.find((p) => p.value === v.installedPeriod);
  return opt ? `Instalado: ${opt.label.toLowerCase()}` : null;
}

/**
 * Chips de filtro ATIVO — visíveis mesmo com o painel avançado fechado. Sem
 * isso o operador do turno seguinte abre a tela com filtros herdados, vê 4
 * dispositivos e reporta queda de frota.
 * @returns {{key:string,label:string}[]}
 */
export function describeActiveFilters(view, options = {}, ctx = null) {
  const v = { ...DEVICE_QUERY_DEFAULTS, ...(view || {}) };
  const out = [];
  if (v.q) out.push({ key: "q", label: `Busca: "${v.q}"` });
  if (v.status !== ALL) {
    const label = { online: "Online", degraded: "Degradado", offline: "Offline" };
    out.push({ key: "status", label: `Status: ${label[v.status] || v.status}` });
  }
  if (v.protectedOnly) out.push({ key: "protectedOnly", label: "Protegidos" });
  const period = installedActive(v) ? periodLabel(v) : null;
  if (period) out.push({ key: "installedPeriod", label: period });

  for (const key of FILTER_KEYS) {
    if (["q", "status", "protectedOnly", "installedPeriod", "installedFrom", "installedTo"].includes(key)) {
      continue;
    }
    if (sameValue(v[key], DEVICE_QUERY_DEFAULTS[key])) continue;
    const axis = AXIS_BY_KEY.get(key);
    if (!axis) continue;
    // A lista derivada é podada por contagem: a opção ATIVA pode ter zerado
    // (o device voltou a bater) e sumido dela. Sem o fallback para o catálogo
    // do eixo o chip viraria o token interno — "gt30d" — justo quando ele é a
    // ÚNICA saída para o operador desfazer um filtro herdado.
    const list = (options[key] && options[key].options) || [];
    const found = list.find((o) => o.value === v[key])
      || (axis.options || []).find((o) => o.value === v[key]);
    out.push({
      key,
      label: axis.chip(v[key], found ? found.label : v[key], ctx),
    });
  }
  return out;
}

/** Zera UM eixo (o "×" do chip de filtro ativo). */
export function clearAxis(view, key) {
  const next = { ...view };
  if (key === "installedPeriod") {
    next.installedPeriod = DEVICE_QUERY_DEFAULTS.installedPeriod;
    next.installedFrom = "";
    next.installedTo = "";
    return next;
  }
  next[key] = Array.isArray(DEVICE_QUERY_DEFAULTS[key])
    ? []
    : DEVICE_QUERY_DEFAULTS[key];
  return next;
}

/**
 * Pipeline completo: filtra e ordena. NUNCA muta a entrada.
 * @param {object[]} source lista vinda do backend
 * @param {object} view estado dos filtros + sortId
 * @param {object} ctx buildFilterContext(source)
 * @param {{now?:number, ixcBoundIds?:Set}} extras
 */
export function applyDeviceQuery(source, view, ctx = null, extras = {}) {
  const list = Array.isArray(source) ? source : [];
  const filtered = list.filter((d) => matchesDeviceQuery(d, view, ctx, extras));
  return sortDevices(filtered, (view && view.sortId) || DEFAULT_SORT_ID);
}
